<script lang="ts">
    import EntitySelector from '$lib/components/common/selectors/EntitySelector.svelte';
    import Button from '$lib/components/common/button/Button.svelte';
    import RequiredBadge from '$lib/components/common/badges/RequiredBadge.svelte';
    import FieldHint from '$lib/components/drawers/client/integrations/FieldHint.svelte';
    import {showToast} from '$lib/helpers/toastrHelper';
    import {fetchWithAuth} from '$lib/utils/http/httpUtil';
    import {Eye, EyeSlash} from 'phosphor-svelte';

    export let clientId: string = '';
  export let entityId: string = '';
  export let clientName: string = '';
  export let organisationId: string | undefined;

  interface CompassEntityConfig {
    compassOdooHost: string;
    compassApiKey: string;
    canInvoicePost: boolean;
    canExpenseClaimPost: boolean;
    canStatementPost: boolean;
    externalOrganisationId: number | null;
  }
  interface CompassExistingConfigResponse {
    exists: boolean;
    config: CompassEntityConfig | null;
  }

  const SECRET_PLACEHOLDER = '••••••';
  const MAX_INT32 = 2147483647;

  let selectedEntityId = entityId;
  let checkedEntityId = '';
  let isLoading = false;
  let isSaving = false;
  let exists = false;
  let showApiKey = false;

  let compassOdooHost = '';
  let compassApiKey = '';
  let canInvoicePost = false;
  let canExpenseClaimPost = false;
  let canStatementPost = false;
  // Odoo res.company id; null (empty box) is not sent to Compass, which then uses the API user's company
  let externalOrganisationId: number | null = 1;
  let externalOrganisationIdInput: HTMLInputElement | null = null;

  $: apiKeyIsHidden = compassApiKey === SECRET_PLACEHOLDER;
  $: if (selectedEntityId && selectedEntityId !== checkedEntityId) loadConfig(selectedEntityId);

  function resetForm() {
    compassOdooHost = '';
    compassApiKey = '';
    canInvoicePost = false;
    canExpenseClaimPost = false;
    canStatementPost = false;
    externalOrganisationId = 1;
    showApiKey = false;
  }

  async function loadConfig(targetEntityId: string) {
    isLoading = true;
    try {
      const response = await fetchWithAuth(`/api/compass/config/${clientId}/${targetEntityId}`);
      const result: CompassExistingConfigResponse = await response.json();
      if (selectedEntityId !== targetEntityId) return;

      exists = result.exists;
      if (result.config) {
        compassOdooHost = result.config.compassOdooHost;
        compassApiKey = result.config.compassApiKey;
        canInvoicePost = result.config.canInvoicePost;
        canExpenseClaimPost = result.config.canExpenseClaimPost;
        canStatementPost = result.config.canStatementPost;
        externalOrganisationId = result.config.externalOrganisationId ?? null;
      } else {
        resetForm();
      }
      checkedEntityId = targetEntityId;
    } catch {
      showToast({ title: 'Error', message: 'Failed to load Compass configuration', type: 'error' });
    } finally {
      isLoading = false;
    }
  }

  function resetAll() {
    resetForm();
    selectedEntityId = '';
    checkedEntityId = '';
  }

  function validationError(): string | null {
    if (!compassOdooHost.trim()) return 'Enter the Compass (Odoo) host';
    if (!exists && !compassApiKey.trim()) return 'Enter an API key';
    // A half-typed number (e.g. "e", "1e", "-") reads as empty, which would otherwise save null and clear the stored value
    if (externalOrganisationIdInput?.validity.badInput) return 'External organisation ID must be a whole number of 1 or more';
    if (externalOrganisationId !== null && !(Number.isInteger(externalOrganisationId) && externalOrganisationId >= 1 && externalOrganisationId <= MAX_INT32)) {
      return 'External organisation ID must be a whole number of 1 or more';
    }
    return null;
  }

  async function handleConnect() {
    const error = validationError();
    if (error) {
      showToast({ title: 'Error', message: error, type: 'error' });
      return;
    }

    isSaving = true;
    try {
      const response = await fetchWithAuth('/api/compass/setup', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          clientId: Number(clientId),
          entityId: Number(selectedEntityId),
          compassOdooHost,
          compassApiKey,
          externalOrganisationId,
        }),
      });
      const result = await response.json();
      if (!response.ok || result.success === false) {
        showToast({ title: 'Error', message: result.errorMessage ?? result.detail ?? 'Failed to connect Compass', type: 'error' });
        return;
      }
      showToast({ title: 'Connected', message: 'Compass connected', type: 'success' });
      checkedEntityId = '';
      await loadConfig(selectedEntityId);
    } catch {
      showToast({ title: 'Error', message: 'Failed to connect Compass', type: 'error' });
    } finally {
      isSaving = false;
    }
  }

  async function handleSave() {
    const error = validationError();
    if (error) {
      showToast({ title: 'Error', message: error, type: 'error' });
      return;
    }

    isSaving = true;
    try {
      const response = await fetchWithAuth(`/api/compass/config/${clientId}/${selectedEntityId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          compassOdooHost,
          compassApiKey: compassApiKey.trim() && compassApiKey !== SECRET_PLACEHOLDER ? compassApiKey : null,
          canInvoicePost,
          canExpenseClaimPost,
          canStatementPost,
          // Always sent: unlike the other settings, the API applies this one as received (null clears it)
          externalOrganisationId,
        }),
      });
      const result = await response.json();
      if (!response.ok || result.success === false) {
        showToast({ title: 'Error', message: result.errorMessage ?? result.detail ?? 'Failed to save Compass configuration', type: 'error' });
        return;
      }
      showToast({ title: 'Saved', message: 'Compass configuration saved', type: 'success' });
      checkedEntityId = '';
      await loadConfig(selectedEntityId);
    } catch {
      showToast({ title: 'Error', message: 'Failed to save Compass configuration', type: 'error' });
    } finally {
      isSaving = false;
    }
  }
</script>

<div class="space-y-4 p-1">
  <EntitySelector bind:selectedEntityId {clientId} isRequired={true} />

  {#if !selectedEntityId}
    <div class="rounded-lg bg-gray-100 p-4 text-sm text-gray-600">Select an entity to configure Compass.</div>
  {:else if isLoading}
    <div class="text-sm text-gray-500">Loading…</div>
  {:else}
    <div class="rounded-lg border bg-white p-4">
      <p class="mb-3 text-sm font-medium">{exists ? 'Connection' : 'Connect Compass'}</p>
      {#if !exists}
        <p class="mb-4 text-sm text-gray-600">This entity has no Compass configuration yet.</p>
      {/if}

      <label class="mb-2 block text-sm text-gray-600" for="compass-odoo-host">
        Odoo host <RequiredBadge />
      </label>
      <input
        id="compass-odoo-host"
        type="text"
        bind:value={compassOdooHost}
        placeholder="https://school.compassfms.com"
        class="mb-3 w-full rounded-md border px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
      />

      <label class="mb-2 block text-sm text-gray-600" for="compass-api-key">
        API key {#if !exists}<RequiredBadge />{/if}
      </label>
      {#if apiKeyIsHidden}
        <div class="flex items-center gap-2">
          <div class="flex-1 rounded-md border border-gray-300 bg-gray-100 px-3 py-2 text-sm italic text-gray-500">API key is set but not visible</div>
          <Button
            label="Change"
            onClick={() => {
              compassApiKey = '';
              showApiKey = false;
            }}
            colour="secondary"
            size="small"
          />
        </div>
      {:else}
        <div class="relative">
          {#if showApiKey}
            <input
              id="compass-api-key"
              type="text"
              bind:value={compassApiKey}
              placeholder="Odoo REST API key"
              class="w-full rounded-md border px-3 py-2 pr-10 focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          {:else}
            <input
              id="compass-api-key"
              type="password"
              bind:value={compassApiKey}
              placeholder="Odoo REST API key"
              class="w-full rounded-md border px-3 py-2 pr-10 focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          {/if}
          <button
            type="button"
            class="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
            on:click={() => (showApiKey = !showApiKey)}
          >
            {#if showApiKey}<EyeSlash size={20} />{:else}<Eye size={20} />{/if}
          </button>
        </div>
      {/if}

      <label class="mb-2 mt-3 block text-sm text-gray-600" for="compass-external-organisation-id">External organisation ID</label>
      <FieldHint text="The Odoo company (res.company) ID that documents are posted to." />
      <input
        id="compass-external-organisation-id"
        type="number"
        min="1"
        step="1"
        inputmode="numeric"
        bind:value={externalOrganisationId}
        bind:this={externalOrganisationIdInput}
        placeholder="Defaults to 1. Leave empty to not send it (Compass then uses the API user's company)"
        class="w-full rounded-md border px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
      />
    </div>

    {#if exists}
      <div class="rounded-lg border bg-white p-4">
        <p class="mb-3 text-sm font-medium">Posting settings</p>
        <div class="grid gap-x-6 gap-y-3 md:grid-cols-3">
          <div>
            <label class="flex cursor-pointer items-center gap-2 text-sm">
              <input type="checkbox" bind:checked={canInvoicePost} class="rounded" disabled={isSaving} />
              Invoice posting
            </label>
            <FieldHint text="Post approved invoices to Compass as vendor bills." />
          </div>
          <div>
            <label class="flex cursor-pointer items-center gap-2 text-sm">
              <input type="checkbox" bind:checked={canExpenseClaimPost} class="rounded" disabled={isSaving} />
              Expense claim posting
            </label>
            <FieldHint text="Post approved expense claims to Compass as vendor bills." />
          </div>
          <div>
            <label class="flex cursor-pointer items-center gap-2 text-sm">
              <input type="checkbox" bind:checked={canStatementPost} class="rounded" disabled={isSaving} />
              Statement posting
            </label>
            <FieldHint text="Post approved credit card statements to Compass as vendor bills." />
          </div>
        </div>
      </div>
    {/if}

    <div class="flex justify-end gap-3">
      <Button label="Reset All" onClick={resetAll} disabled={isSaving} colour="secondary" />
      <Button
        label={isSaving ? 'Saving...' : exists ? 'Update Configuration' : 'Save Configuration'}
        onClick={exists ? handleSave : handleConnect}
        disabled={isSaving}
      />
    </div>
  {/if}
</div>
